import React, { useEffect, useState } from 'react';
import { Download, Upload, ShieldCheck, Warning, CircleNotch, Database, ArrowClockwise } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';

const API = `${window.location.origin}/api`;

export default function BackupTab() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importMode, setImportMode] = useState('merge');
    const [importResult, setImportResult] = useState(null);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/backup/stats`);
            setStats(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchStats(); }, []);

    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await axios.get(`${API}/backup/export`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            const now = new Date();
            const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
            a.href = url;
            a.download = `wyn2026-backup-${stamp}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Błąd podczas eksportu kopii zapasowej');
        } finally {
            setExporting(false);
        }
    };

    const handleImport = async () => {
        if (!importFile) return alert('Wybierz plik ZIP');
        const confirmMsg = importMode === 'replace'
            ? 'UWAGA: Tryb REPLACE usunie wszystkie obecne dane i zastąpi je danymi z kopii. Kontynuować?'
            : 'Tryb MERGE: dane z kopii zostaną nałożone na obecne (upsert po ID). Kontynuować?';
        if (!window.confirm(confirmMsg)) return;
        setImporting(true);
        setImportResult(null);
        try {
            const fd = new FormData();
            fd.append('file', importFile);
            fd.append('mode', importMode);
            fd.append('confirm', 'I-UNDERSTAND');
            const { data } = await axios.post(`${API}/backup/import`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setImportResult(data);
            fetchStats();
        } catch (err) {
            alert(err.response?.data?.detail || 'Błąd podczas importu');
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6 px-3 sm:px-0" data-testid="backup-tab">
            <div>
                <div className="overline mb-1">Bezpieczeństwo danych</div>
                <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight flex items-center gap-2">
                    <ShieldCheck className="w-6 h-6" weight="bold" /> Kopie zapasowe
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Regularnie pobieraj kopię zapasową i przechowuj ją w bezpiecznym miejscu (dysk zewnętrzny, chmura). Każda kopia to spakowany plik ZIP z wszystkimi kolekcjami finansowymi.
                </p>
            </div>

            {/* Stats */}
            <div className="border border-border bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                        <Database className="w-5 h-5" weight="bold" /> Stan bazy danych
                    </h3>
                    <button onClick={fetchStats} className="btn-secondary flex items-center gap-1.5 text-xs" data-testid="backup-refresh">
                        <ArrowClockwise className="w-3.5 h-3.5" /> Odśwież
                    </button>
                </div>
                {loading ? (
                    <div className="text-center py-6"><CircleNotch className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
                ) : stats ? (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {stats.collections.map(c => (
                                <div key={c.collection} className="border border-border p-3">
                                    <div className="overline">{c.collection}</div>
                                    <div className="num text-xl font-medium mt-1">{c.count.toLocaleString('pl-PL')}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 pt-4 border-t-2 border-foreground flex items-baseline justify-between">
                            <span className="overline">Łącznie dokumentów</span>
                            <span className="num text-2xl font-semibold">{stats.total_documents.toLocaleString('pl-PL')}</span>
                        </div>
                    </>
                ) : null}
            </div>

            {/* Export */}
            <div className="border border-border bg-white p-5">
                <h3 className="font-heading font-semibold text-lg mb-2 flex items-center gap-2">
                    <Download className="w-5 h-5" weight="bold" /> Pobierz kopię zapasową
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                    Zalecane: raz w tygodniu. Plik ZIP zawiera wszystkie wynagrodzenia, kredyty, leasingi, pojazdy, urządzenia, dokumenty i ewidencję.
                </p>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={handleExport}
                        disabled={exporting}
                        className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        data-testid="backup-export-btn"
                    >
                        {exporting ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {exporting ? 'Generowanie…' : 'Pobierz backup.zip'}
                    </button>
                    <button
                        onClick={async () => {
                            if (!window.confirm('Wysłać kopię zapasową na email administratora teraz?')) return;
                            try {
                                await axios.post(`${API}/backup/send-now`);
                                alert('Backup wysłany na email administratora');
                            } catch (err) {
                                alert(err.response?.data?.detail || 'Błąd wysyłki');
                            }
                        }}
                        className="btn-secondary flex items-center gap-2"
                        data-testid="backup-send-email-btn"
                    >
                        📧 Wyślij na email teraz
                    </button>
                </div>
                <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
                    <b className="text-foreground">Automatyzacja:</b> co poniedziałek o 08:00 (Europe/Warsaw) system wysyła kopię ZIP na email administratora.
                </div>
            </div>

            {/* Import */}
            <div className="border border-signal/40 bg-white p-5">
                <h3 className="font-heading font-semibold text-lg mb-2 flex items-center gap-2 text-signal">
                    <Warning className="w-5 h-5" weight="bold" /> Przywracanie kopii
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                    Wgraj plik ZIP wygenerowany wcześniej. <b>Tryb Merge</b> — bezpieczny (nakłada dane po ID, zachowuje nowe wpisy). <b>Replace</b> — usuwa całą kolekcję i wstawia od nowa.
                </p>
                <div className="space-y-3">
                    <div>
                        <label className="field-label block mb-1.5">Plik kopii</label>
                        <input
                            type="file"
                            accept=".zip,application/zip"
                            onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                            className="input-field"
                            data-testid="backup-import-file"
                        />
                    </div>
                    <div>
                        <label className="field-label block mb-1.5">Tryb</label>
                        <div className="flex border border-border">
                            <button
                                onClick={() => setImportMode('merge')}
                                className={`flex-1 px-3 py-2 text-xs uppercase font-semibold tracking-wide transition-colors ${importMode === 'merge' ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted'}`}
                                data-testid="backup-mode-merge"
                            >
                                Merge (bezpieczny)
                            </button>
                            <button
                                onClick={() => setImportMode('replace')}
                                className={`flex-1 px-3 py-2 text-xs uppercase font-semibold tracking-wide transition-colors border-l border-border ${importMode === 'replace' ? 'bg-signal text-white' : 'text-muted-foreground hover:bg-muted'}`}
                                data-testid="backup-mode-replace"
                            >
                                Replace (usuwa)
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={handleImport}
                        disabled={importing || !importFile}
                        className="flex items-center gap-2 px-4 py-2 bg-signal text-white text-sm font-semibold uppercase tracking-wide disabled:opacity-50 hover:bg-signal/90"
                        data-testid="backup-import-btn"
                    >
                        {importing ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {importing ? 'Przywracanie…' : 'Przywróć z kopii'}
                    </button>
                </div>

                {importResult && (
                    <div className="mt-4 border-t border-border pt-4">
                        <div className="overline mb-2">Wynik importu</div>
                        <div className="text-xs num space-y-1">
                            {(importResult.results || []).map((r, i) => (
                                <div key={i} className="flex justify-between border-b border-border/50 py-1">
                                    <span>{r.collection}</span>
                                    <span className="text-muted-foreground">
                                        {r.skipped ? 'pominięto' : r.error ? `błąd: ${r.error}` : `wstawiono: ${r.inserted || 0} · upsert: ${r.upserted || 0}`}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Recommendations */}
            <div className="border border-border bg-muted/50 p-4">
                <div className="overline mb-2">Zalecenia bezpieczeństwa</div>
                <ul className="text-sm space-y-1.5">
                    <li>· Pobieraj kopię <b>przynajmniej raz w tygodniu</b> — najlepiej po zamknięciu okresu wynagrodzeń (15-go i 30-go)</li>
                    <li>· Przechowuj kopie w <b>co najmniej dwóch miejscach</b> (np. dysk komputera + Google Drive / Dropbox)</li>
                    <li>· Testuj przywracanie <b>na kopii</b> — sprawdź czy plik ZIP otwiera się poprawnie</li>
                    <li>· Rotacja: zachowuj ostatnie 4 tygodnie + jedna miesięczna kopia z każdego miesiąca</li>
                </ul>
            </div>
        </div>
    );
}
